/**
 * Week-based 4-4-5 / 4-5-4 / 5-4-4 fiscal calendars.
 *
 * A period is a whole number of weeks, a quarter is 13 weeks, and a standard
 * year is 52 weeks = 364 days. Period 1 therefore always begins on a fixed
 * weekday — never on a fixed calendar date. The occasional 53rd week is
 * appended to the last period of Q4 so the calendar can re-anchor to the sun.
 *
 * Weekdays are ISO-8601: 1 = Monday … 7 = Sunday, matching scheduling windows.
 */

export type FiscalPattern = '4-4-5' | '4-5-4' | '5-4-4';

export type FiscalAnchorRule = 'first_of_month' | 'on_or_after' | 'last_before_or_on' | 'nearest' | 'iso_week';

/** How the year boundary is specified. ISO week always behaves as period_start. */
export type FiscalDefinedBy = 'year_end' | 'period_start';

export type FiscalExtraWeek = 'last_period';

export interface FiscalPeriodConfig {
  readonly pattern: FiscalPattern;
  /** ISO weekday every period starts on. 1 = Monday … 7 = Sunday. */
  readonly weekday: number;
  readonly defined_by: FiscalDefinedBy;
  readonly anchor_rule: FiscalAnchorRule;
  /** 1–12. Need not be January. */
  readonly anchor_month: number;
  /** 1–31. Ignored by `first_of_month` and `iso_week` (the latter uses the 4th). */
  readonly anchor_day: number;
  readonly extra_week: FiscalExtraWeek;
}

export interface FiscalPeriod {
  readonly period: number;
  readonly quarter: number;
  readonly weeks: number;
  readonly start: string;
  readonly end: string;
}

export interface FiscalYearPreview {
  readonly year: number;
  readonly start: string;
  readonly end: string;
  readonly weeks: number;
  readonly periods: readonly FiscalPeriod[];
}

export interface FiscalPeriodPreset {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly config: FiscalPeriodConfig;
}

const PATTERNS: readonly FiscalPattern[] = ['4-4-5', '4-5-4', '5-4-4'];
const ANCHOR_RULES: readonly FiscalAnchorRule[] = ['first_of_month', 'on_or_after', 'last_before_or_on', 'nearest', 'iso_week'];
const DEFINED_BY: readonly FiscalDefinedBy[] = ['year_end', 'period_start'];

const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

export const FISCAL_PATTERN_OPTIONS: readonly {
  readonly value: FiscalPattern;
  readonly weeks: readonly [number, number, number];
  readonly rationale: string;
}[] = [
  { value: '4-4-5', weeks: [4, 4, 5], rationale: 'Long period closes the quarter' },
  { value: '4-5-4', weeks: [4, 5, 4], rationale: 'US retail (NRF) standard' },
  { value: '5-4-4', weeks: [5, 4, 4], rationale: 'Long period opens the quarter' },
];

export const FISCAL_WEEKDAY_OPTIONS: readonly { readonly value: number; readonly label: string }[] = WEEKDAY_NAMES.map((label, i) => ({ value: i + 1, label }));

export const FISCAL_MONTH_OPTIONS: readonly { readonly value: number; readonly label: string }[] = MONTH_NAMES.map((label, i) => ({ value: i + 1, label }));

export const FISCAL_ANCHOR_RULE_OPTIONS: readonly {
  readonly value: FiscalAnchorRule;
  readonly label: string;
  readonly hint: string;
}[] = [
  { value: 'first_of_month', label: 'First of the month', hint: 'The first chosen weekday on or after the 1st. Always lands in the anchor month (days 1–7).' },
  { value: 'on_or_after', label: 'On or after the date', hint: 'The chosen weekday falling on the date, else the next one (date … date+6).' },
  {
    value: 'last_before_or_on',
    label: 'Last on or before the date',
    hint: 'The chosen weekday on the date, else the previous one. Can land in the prior month.',
  },
  {
    value: 'nearest',
    label: 'Nearest to the date',
    hint: 'Closest chosen weekday in either direction (±3 days). The dominant retail rule; can land in the prior month.',
  },
  {
    value: 'iso_week',
    label: 'ISO week (contains the 4th)',
    hint: 'Weekday of the week containing the 4th of the anchor month. Earliest start is the 29th of the previous month.',
  },
];

export const DEFAULT_FISCAL_PERIOD_CONFIG: FiscalPeriodConfig = {
  pattern: '4-4-5',
  weekday: 1,
  defined_by: 'period_start',
  anchor_rule: 'iso_week',
  anchor_month: 1,
  anchor_day: 4,
  extra_week: 'last_period',
};

export const FISCAL_PERIOD_PRESETS: readonly FiscalPeriodPreset[] = [
  {
    id: 'iso-445',
    label: 'ISO week year (4-4-5)',
    description: 'Monday of the week containing 4 January. Period 1 may start in late December.',
    config: DEFAULT_FISCAL_PERIOD_CONFIG,
  },
  {
    id: 'nrf-454',
    label: 'US retail (NRF 4-5-4)',
    description: 'Year ends on the Saturday nearest 31 January. Periods start Sunday (~1 February).',
    config: {
      pattern: '4-5-4',
      weekday: 7,
      defined_by: 'year_end',
      anchor_rule: 'nearest',
      anchor_month: 1,
      anchor_day: 31,
      extra_week: 'last_period',
    },
  },
  {
    id: 'first-monday-jan',
    label: 'First Monday of January (4-4-5)',
    description: 'Period 1 always starts in January, on the first Monday.',
    config: {
      pattern: '4-4-5',
      weekday: 1,
      defined_by: 'period_start',
      anchor_rule: 'first_of_month',
      anchor_month: 1,
      anchor_day: 1,
      extra_week: 'last_period',
    },
  },
  {
    id: 'first-sunday-jan',
    label: 'First Sunday of January (4-4-5)',
    description: 'Period 1 always starts in January, on the first Sunday.',
    config: {
      pattern: '4-4-5',
      weekday: 7,
      defined_by: 'period_start',
      anchor_rule: 'first_of_month',
      anchor_month: 1,
      anchor_day: 1,
      extra_week: 'last_period',
    },
  },
  {
    id: 'sunday-nearest-jan1',
    label: 'Sunday nearest 1 January (4-4-5)',
    description: 'Period 1 can fall back into December. Same dates as ISO-Sunday in most years, but not all.',
    config: {
      pattern: '4-4-5',
      weekday: 7,
      defined_by: 'period_start',
      anchor_rule: 'nearest',
      anchor_month: 1,
      anchor_day: 1,
      extra_week: 'last_period',
    },
  },
  {
    id: 'last-sunday-dec',
    label: 'Last Sunday of December (4-4-5)',
    description: 'Period 1 is the last Sunday of December of the labelled year.',
    config: {
      pattern: '4-4-5',
      weekday: 7,
      defined_by: 'period_start',
      anchor_rule: 'last_before_or_on',
      anchor_month: 12,
      anchor_day: 31,
      extra_week: 'last_period',
    },
  },
  {
    id: 'april-iso',
    label: 'April year, ISO week (4-4-5)',
    description: 'Monday of the week containing 4 April — UK / Japan / India / South Africa style year.',
    config: {
      pattern: '4-4-5',
      weekday: 1,
      defined_by: 'period_start',
      anchor_rule: 'iso_week',
      anchor_month: 4,
      anchor_day: 4,
      extra_week: 'last_period',
    },
  },
  {
    id: 'july-iso',
    label: 'July year, ISO week (4-4-5)',
    description: 'Monday of the week containing 4 July — Australia-style year.',
    config: {
      pattern: '4-4-5',
      weekday: 1,
      defined_by: 'period_start',
      anchor_rule: 'iso_week',
      anchor_month: 7,
      anchor_day: 4,
      extra_week: 'last_period',
    },
  },
  {
    id: 'october-iso',
    label: 'October year, ISO week (4-4-5)',
    description: 'Monday of the week containing 4 October — US federal-style year.',
    config: {
      pattern: '4-4-5',
      weekday: 1,
      defined_by: 'period_start',
      anchor_rule: 'iso_week',
      anchor_month: 10,
      anchor_day: 4,
      extra_week: 'last_period',
    },
  },
];

export const weekdayName = (weekday: number): string => WEEKDAY_NAMES[weekday - 1] ?? String(weekday);

export const monthName = (month: number): string => MONTH_NAMES[month - 1] ?? String(month);

/** The weekday a year ends on, given the weekday periods start on. */
export const yearEndWeekday = (periodStartWeekday: number): number => (periodStartWeekday === 1 ? 7 : periodStartWeekday - 1);

export const weeksForPattern = (pattern: FiscalPattern): readonly [number, number, number] =>
  FISCAL_PATTERN_OPTIONS.find((p) => p.value === pattern)?.weeks ?? [4, 4, 5];

/**
 * Longest period length in days for a pattern when a 53-week year appends the
 * extra week to period 12 (`extra_week: 'last_period'`). Period 12 always uses
 * the third quarter slot, so 4-4-5 reaches 6 weeks = 42 days; 4-5-4 / 5-4-4
 * stay at 5 weeks = 35 days.
 */
export const maxPeriodDaysForPattern = (pattern: FiscalPattern): number => {
  const [, , lastSlotWeeks] = weeksForPattern(pattern);
  return (lastSlotWeeks + 1) * 7;
};

/** Flat API/UI bound covering every shipped pattern (dominated by 4-4-5 → 42). */
export const MAX_FISCAL_PERIOD_DAYS = Math.max(...PATTERNS.map((p) => maxPeriodDaysForPattern(p)));

export const ruleUsesAnchorDay = (rule: FiscalAnchorRule): boolean => rule === 'on_or_after' || rule === 'last_before_or_on' || rule === 'nearest';

const isPattern = (value: unknown): value is FiscalPattern => PATTERNS.includes(value as FiscalPattern);
const isRule = (value: unknown): value is FiscalAnchorRule => ANCHOR_RULES.includes(value as FiscalAnchorRule);
const isDefinedBy = (value: unknown): value is FiscalDefinedBy => DEFINED_BY.includes(value as FiscalDefinedBy);

const asInt = (raw: unknown, fallback: number, min: number, max: number): number => {
  const parsed = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
  if (!Number.isInteger(parsed) || parsed < min || parsed > max) return fallback;
  return parsed;
};

/** Days in `month` (1–12) for config validation / UI max.
 *
 * Uses a leap year so February may be 29 — configs may store that day. When
 * resolving a concrete year, {@link resolveAnchorDate} clamps via `utcDate` to
 * that year's last day of February (28 in common years). Operators choosing
 * day 29 should expect that one-day drift; the editor surfaces it in help text
 * and {@link describeFiscalPeriodConfig}.
 */
export const daysInMonth = (month: number): number => new Date(Date.UTC(2024, month, 0)).getUTCDate();

export function isFiscalPeriodConfig(value: unknown): value is FiscalPeriodConfig {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  const month = record.anchor_month as number;
  const day = record.anchor_day as number;
  return (
    isPattern(record.pattern) &&
    Number.isInteger(record.weekday) &&
    (record.weekday as number) >= 1 &&
    (record.weekday as number) <= 7 &&
    isDefinedBy(record.defined_by) &&
    isRule(record.anchor_rule) &&
    Number.isInteger(month) &&
    month >= 1 &&
    month <= 12 &&
    Number.isInteger(day) &&
    day >= 1 &&
    day <= daysInMonth(month) &&
    record.extra_week === 'last_period'
  );
}

export function normalizeFiscalPeriodConfig(value: unknown): FiscalPeriodConfig {
  const raw = value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  const weekday = asInt(raw.weekday, DEFAULT_FISCAL_PERIOD_CONFIG.weekday, 1, 7);
  const anchor_rule = isRule(raw.anchor_rule) ? raw.anchor_rule : DEFAULT_FISCAL_PERIOD_CONFIG.anchor_rule;
  const anchor_month = asInt(raw.anchor_month, DEFAULT_FISCAL_PERIOD_CONFIG.anchor_month, 1, 12);
  const maxDay = daysInMonth(anchor_month);
  return {
    pattern: isPattern(raw.pattern) ? raw.pattern : DEFAULT_FISCAL_PERIOD_CONFIG.pattern,
    weekday,
    defined_by: anchor_rule === 'iso_week' ? 'period_start' : isDefinedBy(raw.defined_by) ? raw.defined_by : DEFAULT_FISCAL_PERIOD_CONFIG.defined_by,
    anchor_rule,
    anchor_month,
    anchor_day: asInt(raw.anchor_day, Math.min(DEFAULT_FISCAL_PERIOD_CONFIG.anchor_day, maxDay), 1, maxDay),
    extra_week: 'last_period',
  };
}

const utcDate = (year: number, month: number, day: number): Date => {
  const lastDay = new Date(Date.UTC(year, month, 0, 12, 0, 0)).getUTCDate();
  return new Date(Date.UTC(year, month - 1, Math.min(day, lastDay), 12, 0, 0));
};

export const formatYmd = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const isoWeekdayOf = (date: Date): number => {
  const jsWeekday = date.getUTCDay();
  return jsWeekday === 0 ? 7 : jsWeekday;
};

const daysBetweenInclusive = (start: Date, end: Date): number => Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;

const firstOnOrAfter = (date: Date, weekday: number): Date => {
  const delta = (weekday - isoWeekdayOf(date) + 7) % 7;
  return addDays(date, delta);
};

const lastOnOrBefore = (date: Date, weekday: number): Date => {
  const delta = (isoWeekdayOf(date) - weekday + 7) % 7;
  return addDays(date, -delta);
};

const nearestWeekday = (date: Date, weekday: number): Date => {
  const toNext = (weekday - isoWeekdayOf(date) + 7) % 7;
  const toPrev = (isoWeekdayOf(date) - weekday + 7) % 7;
  if (toNext === 0) return date;
  return toNext <= toPrev ? addDays(date, toNext) : addDays(date, -toPrev);
};

const weekContaining = (date: Date, weekStartWeekday: number): Date => lastOnOrBefore(date, weekStartWeekday);

/**
 * The chosen weekday selected by `rule` around the reference date in `year`.
 */
export function resolveAnchorDate(year: number, weekday: number, rule: FiscalAnchorRule, month: number, day: number): Date {
  switch (rule) {
    case 'first_of_month':
      return firstOnOrAfter(utcDate(year, month, 1), weekday);
    case 'on_or_after':
      return firstOnOrAfter(utcDate(year, month, day), weekday);
    case 'last_before_or_on':
      return lastOnOrBefore(utcDate(year, month, day), weekday);
    case 'nearest':
      return nearestWeekday(utcDate(year, month, day), weekday);
    case 'iso_week':
      return weekContaining(utcDate(year, month, 4), weekday);
  }
}

export function fiscalYearBounds(config: FiscalPeriodConfig, year: number): { readonly start: Date; readonly end: Date } {
  const { weekday, anchor_rule, anchor_month, anchor_day } = config;
  // ISO week always names period 1 (the week containing the 4th). Defining it
  // from the year-end would just be "the day before next year's ISO start".
  const definedBy = anchor_rule === 'iso_week' ? 'period_start' : config.defined_by;

  if (definedBy === 'year_end') {
    const endWeekday = yearEndWeekday(weekday);
    const end = resolveAnchorDate(year, endWeekday, anchor_rule, anchor_month, anchor_day);
    const prevEnd = resolveAnchorDate(year - 1, endWeekday, anchor_rule, anchor_month, anchor_day);
    return { start: addDays(prevEnd, 1), end };
  }

  const start = resolveAnchorDate(year, weekday, anchor_rule, anchor_month, anchor_day);
  const nextStart = resolveAnchorDate(year + 1, weekday, anchor_rule, anchor_month, anchor_day);
  return { start, end: addDays(nextStart, -1) };
}

export function buildFiscalYear(config: FiscalPeriodConfig, year: number): FiscalYearPreview {
  const { start, end } = fiscalYearBounds(config, year);
  const totalDays = daysBetweenInclusive(start, end);
  const weeks = Math.round(totalDays / 7);
  const quarterWeeks = weeksForPattern(config.pattern);
  const extra = weeks > 52 ? weeks - 52 : 0;
  // Which 0-based period index receives the 53rd week. Today only 'last_period'
  // is shipped; keep this as the seam for a future second placement.
  const extraPeriodIndex = 11;

  const periods: FiscalPeriod[] = [];
  let cursor = start;
  for (let i = 0; i < 12; i += 1) {
    const quarter = Math.floor(i / 3) + 1;
    const periodWeeks = quarterWeeks[i % 3] + (i === extraPeriodIndex ? extra : 0);
    const periodEnd = addDays(cursor, periodWeeks * 7 - 1);
    periods.push({
      period: i + 1,
      quarter,
      weeks: periodWeeks,
      start: formatYmd(cursor),
      end: formatYmd(periodEnd),
    });
    cursor = addDays(periodEnd, 1);
  }

  return { year, start: formatYmd(start), end: formatYmd(end), weeks, periods };
}

export function buildFiscalYearRange(config: FiscalPeriodConfig, fromYear: number, toYear: number): readonly FiscalYearPreview[] {
  const years: FiscalYearPreview[] = [];
  for (let y = fromYear; y <= toYear; y += 1) years.push(buildFiscalYear(config, y));
  return years;
}

const dayMonth = (day: number, month: number): string => `${day} ${monthName(month)}`;

const ruleClause = (rule: FiscalAnchorRule, weekday: string, month: number, day: number): string => {
  const monthLabel = monthName(month);
  switch (rule) {
    case 'first_of_month':
      return `the first ${weekday} of ${monthLabel}`;
    case 'on_or_after':
      return `the first ${weekday} on or after ${dayMonth(day, month)}`;
    case 'last_before_or_on':
      return `the last ${weekday} on or before ${dayMonth(day, month)}`;
    case 'nearest':
      return `the ${weekday} nearest ${dayMonth(day, month)}`;
    case 'iso_week':
      return `the ${weekday} of the week containing 4 ${monthLabel} (ISO week rule)`;
  }
};

/** One-sentence description of the calendar an operator just configured. */
export function describeFiscalPeriodConfig(config: FiscalPeriodConfig): string {
  const startDay = weekdayName(config.weekday);
  const endDay = weekdayName(yearEndWeekday(config.weekday));
  const extra = 'A 53rd week is appended to the last period of Q4.';
  const feb29Note =
    config.anchor_month === 2 && config.anchor_day === 29 ? ' Anchor day 29 of February is used in leap years and treated as 28 in common years.' : '';

  if (config.anchor_rule === 'iso_week' || config.defined_by === 'period_start') {
    return `${config.pattern} calendar. Periods start on ${startDay}. Period 1 starts on ${ruleClause(config.anchor_rule, startDay, config.anchor_month, config.anchor_day)}. ${extra}${feb29Note}`;
  }

  return `${config.pattern} calendar. Periods start on ${startDay}. Year ends on ${ruleClause(config.anchor_rule, endDay, config.anchor_month, config.anchor_day)}. Period 1 starts the following ${startDay}. ${extra}${feb29Note}`;
}

export function matchingPresetId(config: FiscalPeriodConfig): string | null {
  const match = FISCAL_PERIOD_PRESETS.find(
    (p) =>
      p.config.pattern === config.pattern &&
      p.config.weekday === config.weekday &&
      p.config.defined_by === config.defined_by &&
      p.config.anchor_rule === config.anchor_rule &&
      p.config.anchor_month === config.anchor_month &&
      p.config.anchor_day === config.anchor_day
  );
  return match?.id ?? null;
}
